import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { Mic } from 'lucide-react-native';
import { memo, useEffect, useRef } from 'react';
import { Animated, GestureResponderHandlers, View } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { meterLevel } from '../functions/meterLevel';
import { tint } from '../functions/tint';
import { voiceInputColors } from '../voiceInputColors';

/**
 * The box's mic: a tap dictates into the box (the keyboard stays; a second
 * tap stops), a hold talks as the bar does. Dictating, a ring behind it
 * breathes with the voice.
 */
export const MicButton = memo(
  ({
    dictating,
    canTalk,
    level$,
    panHandlers,
  }: {
    dictating: boolean;
    canTalk: boolean;
    level$: BehaviorSubject<number>;
    panHandlers: GestureResponderHandlers;
  }) => {
    const { rpx } = useResponsiveContext();
    const size = rpx(36);
    // Scaled by the level as it comes, not re-rendered for it.
    const scale = useRef(new Animated.Value(1)).current;
    useEffect(() => {
      const to = (value: number) =>
        Animated.timing(scale, {
          toValue: value,
          duration: 100,
          useNativeDriver: true,
        }).start();
      if (!dictating) {
        to(1);
        return;
      }
      const levels = level$.subscribe(level =>
        to(1.15 + meterLevel(level) * 0.45),
      );
      return () => levels.unsubscribe();
    }, [dictating, level$, scale]);
    return (
      <View
        {...panHandlers}
        accessibilityRole="button"
        accessibilityLabel={dictating ? '结束听写' : '听写，按住说话'}
        style={{
          width: size,
          height: size,
          alignItems: 'center',
          justifyContent: 'center',
          opacity: canTalk || dictating ? 1 : 0.4,
        }}
      >
        <Animated.View
          pointerEvents="none"
          style={{
            position: 'absolute',
            width: size,
            height: size,
            borderRadius: size / 2,
            backgroundColor: tint(voiceInputColors.primary, 0.25),
            opacity: dictating ? 1 : 0,
            transform: [{ scale }],
          }}
        />
        <View
          pointerEvents="none"
          style={{
            width: size,
            height: size,
            borderRadius: size / 2,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: dictating
              ? voiceInputColors.primary
              : 'transparent',
          }}
        >
          <Mic
            size={rpx(18)}
            color={
              dictating
                ? voiceInputColors.primaryForeground
                : voiceInputColors.mutedForeground
            }
          />
        </View>
      </View>
    );
  },
);
